import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { InputComponent } from '../../shared/ui/input/input.component';
import { ManagerCardComponent } from '../manager-card/manager-card.component';
import { ContentUpdateService } from '../services/content-update.service';
import { SpinnerService } from '../../../core/services/spinner.service';
import { Subscription } from 'rxjs';
import { GeneralErrorComponent } from '../../shared/ui/general-error/general-error.component';

@Component({
  selector: 'app-content-managers',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    ReactiveFormsModule,
    InputComponent,
    ManagerCardComponent,
    GeneralErrorComponent,
  ],
  providers: [],
  templateUrl: './content-managers.component.html',
  styleUrl: './content-managers.component.scss',
})
export class ContentManagersComponent implements OnDestroy{
  managersContent!: Manager[];
  @Input() contentForm!: FormGroup;
  @Output() updateManagers: EventEmitter< { managers: Manager[], operation: 'add' | 'email' | 'delete' | 'search' } > = new EventEmitter<
    { managers: Manager[], operation: 'add' | 'email' | 'delete' | 'search' }
  >();

  showList: boolean = false;
  subscription: Subscription = new Subscription();

  managersList!: Manager[];
  resultados!: any[];

  constructor(
    private readonly _managementService: ContentUpdateService,
    private readonly _spinnerService: SpinnerService
  ) {}

  ngOnInit(): void {
    this.managersContent = this.contentForm.get('managers')?.value;

    this.contentForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(0), distinctUntilChanged())
      .subscribe((valor) => {
        this.buscarResponsables(valor);
        // registrar evento search en DataLayer
        this.updateManagers.emit({ managers: this.managersContent, operation: 'search' });
      });

      this.contentForm
      .get('email')
      ?.valueChanges.pipe(debounceTime(0), distinctUntilChanged())
      .subscribe((valor) => {
        this.updateManagers.emit({ managers: this.managersContent, operation: 'email' });
      });

    this.getManagers();
  }

  getManagers(): void {
    this._spinnerService.show();
    const sub = this._managementService.getAllManagers().subscribe({
      next: (response) => {
        const contentResponse: ManagerResponse = response;
        this.managersList = contentResponse.users;
      },
      error: (err) => {
        this._spinnerService.hide();
      },
      complete: () => {
        this._spinnerService.hide();
      },
    });
    this.subscription.add(sub);
  }

  buscarResponsables(valor: string) {
    const searchTerm = valor.trim().toLowerCase();

    if (!searchTerm) {
      this.resultados = [];
      this.showList = false;
      return;
    }

    this.showList = true;
    const managersEmails = new Set(this.managersContent.map((man: Manager) => man.email));

    this.resultados = this.managersList
      .filter((item: Manager) => !managersEmails.has(item.email))
      .filter((responsable: Manager) => responsable.name?.toLowerCase().includes(searchTerm));
  }

  addManager(manager: Manager) {
    this.showList = false;
    this.managersContent.push(manager);
    this.contentForm.get('managers')?.setValue(this.managersContent);
    this.contentForm.get('search')?.setValue('', {emitEvent: false});
    this.updateManagers.emit({ managers: this.managersContent, operation: 'add' });
  }

  deleteManager(manager: Manager) {
    const index = this.managersContent.indexOf(manager);
    if (index !== -1) {
      this.managersContent.splice(index, 1);
      this.contentForm.get('managers')?.setValue(this.managersContent);
      this.updateManagers.emit({ managers: this.managersContent, operation: 'delete' });
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
